import { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Youtube, Linkedin, MessageCircle } from "lucide-react";
import { useNewsletter } from "@/components/marketing/NewsletterSection";
import { useSiteSettings, pick } from "@/lib/siteSettings";

const COLUMNS = [
  {
    title: "Shop",
    links: [
      { label: "All oils", to: "/products" },
      { label: "Track your order", to: "/track-order" },
      { label: "Your account", to: "/account" },
      { label: "Wishlist", to: "/wishlist" },
    ],
  },
  {
    title: "Learn",
    links: [
      { label: "Benefits of cold pressed coconut oil", to: "/cold-pressed-coconut-oil-benefits" },
      { label: "Cold pressed vs refined", to: "/cold-pressed-vs-refined-coconut-oil" },
      { label: "How to spot adulterated oil", to: "/how-to-check-coconut-oil-purity" },
      { label: "Coconut oil for hair", to: "/coconut-oil-for-hair" },
      { label: "Coconut oil for skin", to: "/coconut-oil-for-skin" },
      { label: "Coconut oil vs other oils", to: "/coconut-oil-vs-other-cooking-oils" },
      { label: "Cooking temperatures", to: "/coconut-oil-cooking-temperature" },
      { label: "What the studies say", to: "/coconut-oil-cooking-studies" },
      { label: "Coconut oil in Ayurveda", to: "/ayurvedic-benefits-of-coconut-oil" },
      { label: "Recipes", to: "/recipes" },
      { label: "Journal", to: "/blog" },
    ],
  },
  {
    title: "Karijeeva",
    links: [
      { label: "Our story", to: "/about" },
      { label: "Leadership and governance", to: "/leadership" },
      { label: "Our farms", to: "/the-farm" },
      { label: "How we press", to: "/cold-press-process" },
      { label: "Lab reports", to: "/lab-reports" },
    ],
  },
  {
    title: "Help",
    links: [
      { label: "Contact us", to: "/contact" },
      { label: "FAQs", to: "/faqs" },
      { label: "Shipping", to: "/shipping-policy" },
      { label: "Returns and refunds", to: "/returns-policy" },
    ],
  },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const { subscribe, loading, modal } = useNewsletter();
  const settings = useSiteSettings();

  const onSubscribe = async (e) => {
    e.preventDefault();
    const res = await subscribe(email, "footer");
    if (res?.ok) setEmail("");
  };

  const year = new Date().getFullYear();
  const company = pick(settings, "company_name");
  const cin = pick(settings, "cin");
  const fssai = pick(settings, "fssai_license");
  const supportEmail = pick(settings, "support_email");
  const legalEmail = pick(settings, "legal_email");
  const supportPhone = pick(settings, "support_phone");
  const hoursIst = pick(settings, "hours_ist");
  const registeredAddress = pick(settings, "registered_address");
  const parentSite = pick(settings, "parent_site_url");
  const telHref = (supportPhone || "").replace(/[^+\d]/g, "");

  const link = "text-ink-muted hover:text-brand-ink hover:underline";

  return (
    <>
      <footer className="bg-brand-parchment-soft text-[12.5px] font-body text-ink-muted" data-testid="footer">
        <div className="max-w-6xl mx-auto px-6 pt-12 pb-8">
          <div className="pb-8 border-b border-black/10 grid md:grid-cols-2 gap-6 items-end">
            <div>
              <p className="font-display text-[22px] text-brand-ink tracking-[-0.02em]">Harvest notes, a few times a month.</p>
              <p className="mt-1">New batches, recipes and the odd farm story. 10% off your first order when you join.</p>
            </div>
            <form onSubmit={onSubscribe} className="flex gap-2 md:justify-end" data-testid="newsletter-form">
              <label htmlFor="footer-email" className="sr-only">Email address</label>
              <input
                id="footer-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email address"
                className="h-10 w-full md:w-72 rounded-control border border-black/15 bg-white px-4 text-[14px] text-brand-ink focus:outline-none focus:ring-2 focus:ring-brand-gold"
                data-testid="newsletter-email-input"
              />
              <button type="submit" disabled={loading} className="h-10 rounded-control bg-brand-ink px-5 text-[14px] text-white disabled:opacity-60" data-testid="newsletter-submit">
                {loading ? "Joining" : "Join"}
              </button>
            </form>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 py-8 border-b border-black/10">
            {COLUMNS.map((col) => (
              <nav key={col.title} aria-label={col.title}>
                <p className="font-semibold text-brand-ink mb-3">{col.title}</p>
                <ul className="space-y-2">
                  {col.links.map((l) => (
                    <li key={l.to + l.label}>
                      <Link
                        to={l.to}
                        className={link}
                        data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`}
                      >
                        {l.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>
            ))}
            <div className="col-span-2 md:col-span-1">
              <p className="font-semibold text-brand-ink mb-3">Talk to us</p>
              <ul className="space-y-2">
                {supportEmail && (
                  <li><a href={`mailto:${supportEmail}`} className={link} data-testid="footer-support-email">{supportEmail}</a></li>
                )}
                {supportPhone && (
                  <li><a href={`tel:${telHref}`} className={link} data-testid="footer-support-phone">{supportPhone}</a></li>
                )}
                {hoursIst && <li data-testid="footer-hours">{hoursIst}</li>}
              </ul>
              <div className="mt-4 flex gap-3">
                <Social href={pick(settings, "instagram_url")} label="Instagram"><Instagram /></Social>
                <Social href={pick(settings, "facebook_url")} label="Facebook"><Facebook /></Social>
                <Social href={pick(settings, "youtube_url")} label="YouTube"><Youtube /></Social>
                <Social href={pick(settings, "whatsapp_url")} label="WhatsApp"><MessageCircle /></Social>
                <Social href={pick(settings, "linkedin_url")} label="LinkedIn"><Linkedin /></Social>
              </div>
            </div>
          </div>

          <div className="pt-6 space-y-3 leading-relaxed">
            <p>
              Karijeeva is a brand of{" "}
              {parentSite ? (
                <a href={parentSite} target="_blank" rel="noopener noreferrer" className={link} data-testid="footer-parent-link">{company}</a>
              ) : company}
              . FSSAI licence no. <span data-testid="footer-fssai">{fssai}</span>
              {cin && <> · CIN <span data-testid="footer-cin">{cin}</span></>}
              {registeredAddress && <> · <span data-testid="footer-registered-address">{registeredAddress}</span></>}
            </p>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p data-testid="footer-copyright">Copyright © {year} {company}. All rights reserved.</p>
              <nav className="flex flex-wrap gap-x-4 gap-y-1" aria-label="Legal">
                <Link data-testid="footer-link-privacy" to="/privacy-policy" className={link}>Privacy</Link>
                <Link data-testid="footer-link-terms" to="/terms" className={link}>Terms</Link>
                <Link data-testid="footer-link-shipping" to="/shipping-policy" className={link}>Shipping</Link>
                <Link data-testid="footer-link-refund" to="/returns-policy" className={link}>Refunds</Link>
                <Link data-testid="footer-link-cookies" to="/cookie-policy" className={link}>Cookies</Link>
                {legalEmail && <a data-testid="footer-legal-email" href={`mailto:${legalEmail}`} className={link}>{legalEmail}</a>}
              </nav>
            </div>
          </div>
        </div>
      </footer>
      {modal}
    </>
  );
}

function Social({ href, children, label }) {
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      data-testid={`footer-social-${label.toLowerCase()}`}
      className="text-ink-muted hover:text-brand-ink [&_svg]:w-4 [&_svg]:h-4"
    >
      {children}
    </a>
  );
}
